import React, { useContext, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import MyContext from "../../context/MyContext";
import {
  getSubLocations,
  getPreviousBuildHistory,
} from "../../api/services/scheduling";
import { useSnackbar } from "notistack";
import DynamicSelectInput from "../FormFields/CustomSelect/DynamicSelectInput";
import CustomDate from "../CustomForm/CustomDate";
import { format } from "date-fns";
import { Close } from "@mui/icons-material";

const BuildHistoryModal = ({ show, handleClose, setSublocation, date }) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedSublocation, setSelectedSublocation] = useState("");
  const [previousBuildHistory, setPreviousBuildHistory] = useState(null);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const queryClient = useQueryClient();
  const { locationID } = useContext(MyContext);
  const { enqueueSnackbar } = useSnackbar();

  // Fetch sublocations based on selected date and locationID
  const { data: subLocations, isLoading: subLocationsLoading } = useQuery(
    ["subLocations", locationID, selectedDate],
    () => getSubLocations(locationID, selectedDate),
    {
      enabled: !!locationID && !!selectedDate,
    }
  );

  // Fetch build history based on selected sublocation and date
  const handleShowHistory = async () => {
    if (selectedSublocation) {
      setIsHistoryVisible(true);
      try {
        const history = await getPreviousBuildHistory(
          selectedDate,
          selectedSublocation
        );
        setPreviousBuildHistory(history);
      } catch (error) {
        enqueueSnackbar("Failed to fetch build history", { variant: "error" });
      }
    } else {
      enqueueSnackbar("Please select a sublocation first", {
        variant: "warning",
      });
    }
  };

  const handleListItemClick = (item) => {
    setSublocation(item.template.sublocation);
    date(item.build_at);
    setSelectedSublocation("");
    setPreviousBuildHistory(null);
    setSelectedDate(null);
    handleClose();
  };

  return (
    <Modal
      open={show}
      onClose={handleClose}
      aria-labelledby="build-history-modal"
      aria-describedby="build-history-modal-description"
    >
      <Box
        sx={{
          maxWidth: 500,
          mx: "auto",
          mt: 6,
          p: 3,
          backgroundColor: "white",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Show Schedule</Typography>
          <IconButton onClick={handleClose} color="error">
            <Close />
          </IconButton>
        </Box>
        <CustomDate
          label="Select Date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <DynamicSelectInput
          label="Select Area"
          options={subLocations || []}
          loading={subLocationsLoading}
          value={selectedSublocation}
          onChange={(e) => setSelectedSublocation(e.target.value)}
        />

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleShowHistory}
          >
            Show History
          </Button>
        </Box>
        {isHistoryVisible && (
          <Box sx={{ mt: 2 }}>
            {previousBuildHistory &&
              (previousBuildHistory.length > 0 ? (
                previousBuildHistory.map((historyItem) => (
                  <Button
                    key={historyItem.id}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 1 }}
                    onClick={() => handleListItemClick(historyItem)}
                  >
                    {format(new Date(historyItem.build_at), "PPP")} -{" "}
                    {historyItem.template.name}
                  </Button>
                ))
              ) : (
                <Typography>No build history available</Typography>
              ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default BuildHistoryModal;
